import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { CategoriesAPI, DEFAULT_CATEGORIES } from "@/lib/categories";
import { Tags, Plus, X, RotateCcw, Save } from "lucide-react";

const inputClass =
  "bg-[#0E0F13] border-white/10 text-white placeholder:text-zinc-600 focus-visible:border-[#D4AF37] focus-visible:ring-1 focus-visible:ring-[#D4AF37] rounded-md";

export default function CategoriesManager() {
  const [values, setValues] = useState(DEFAULT_CATEGORIES);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    CategoriesAPI.get()
      .then((d) => {
        if (Array.isArray(d?.values) && d.values.length) setValues(d.values);
      })
      .catch(() => {});
  }, []);

  const add = () => {
    const v = draft.trim();
    if (!v) return;
    if (values.some((x) => x.toLowerCase() === v.toLowerCase())) {
      toast.error("Cette catégorie existe déjà");
      return;
    }
    setValues((arr) => [...arr, v]);
    setDraft("");
  };

  const save = async () => {
    setSaving(true);
    try {
      const d = await CategoriesAPI.update(values);
      setValues(d.values);
      toast.success("Catégories enregistrées");
    } catch {
      toast.error("Enregistrement impossible");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="bg-[#0A0B0E] border border-white/5 rounded-md p-4 space-y-4"
      data-testid="categories-manager"
    >
      <div className="flex items-center gap-2 text-overline !text-zinc-400">
        <Tags className="w-3.5 h-3.5 text-[#D4AF37]" /> Catégories
      </div>
      <p className="text-zinc-500 font-body text-sm">
        Ces catégories sont proposées partout (joueurs, équipes) : ajoute, supprime ou
        adapte la liste à tes besoins.
      </p>
      <div className="flex flex-wrap gap-1.5">
        {values.map((c) => (
          <span
            key={c}
            className="inline-flex items-center gap-1.5 border border-white/15 bg-white/[0.04] rounded-full pl-3 pr-1.5 py-1 text-[11px] uppercase tracking-[0.14em] font-bold"
            data-testid={`category-chip-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
          >
            {c}
            <button
              type="button"
              onClick={() => setValues((arr) => arr.filter((x) => x !== c))}
              className="text-zinc-500 hover:text-red-400 rounded-full p-0.5"
              aria-label={`Supprimer ${c}`}
              data-testid={`remove-category-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
      </div>
      <div className="flex flex-wrap gap-2">
        <Input
          className={`${inputClass} w-44`}
          placeholder="Nouvelle catégorie"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add();
            }
          }}
          data-testid="new-category-input"
        />
        <Button
          type="button"
          variant="outline"
          onClick={add}
          data-testid="add-category-btn"
          className="bg-transparent border-white/15 hover:bg-white/5 hover:border-white/40 text-white h-10"
        >
          <Plus className="w-4 h-4 mr-1.5" /> Ajouter
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={() => setValues(DEFAULT_CATEGORIES)}
          data-testid="reset-categories-btn"
          className="bg-transparent border-white/15 hover:bg-white/5 hover:border-white/40 text-zinc-400 h-10"
        >
          <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Liste par défaut
        </Button>
        <Button
          type="button"
          onClick={save}
          disabled={saving}
          data-testid="save-categories-btn"
          className="h-10 px-5 rounded-md font-bold uppercase tracking-[0.18em] text-xs bg-[#D4AF37] text-black hover:bg-[#c9a430]"
        >
          <Save className="w-3.5 h-3.5 mr-1.5" />
          {saving ? "..." : "Enregistrer"}
        </Button>
      </div>
    </div>
  );
}
